import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose, IconEdit, IconPlus, IconTrash } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { fmtMoney } from '../../../common/utils/money';
import { magnitudeLabel } from '../../../core/constants/billing';
import { useCostsList } from '../contexts/costs';
import { useManagementLines, useAddLine, useUpdateLine, useDeleteLine } from '../contexts/managements';

import '../styles/billing.css';

// Composición documental de un manejo (admin-billing-catalog.md): management → cost
// × quantity. El `total` es INFORMATIVO (no es la tarifa que liquida). Agregar el
// mismo costo es idempotente por (management, cost) → actualiza la cantidad.
export default function CompositionModal({ management, onClose }) {
    const id = management.id;
    const { data, isLoading, error } = useManagementLines(id);
    const { data: costsData } = useCostsList({ limit: 500, status: 2, sort: 'name' });
    const { mutateAsync: add, isPending: adding } = useAddLine(id);
    const { mutateAsync: update, isPending: updating } = useUpdateLine(id);
    const { mutateAsync: remove, isPending: removing } = useDeleteLine(id);
    const busy = adding || updating || removing;
    const cardRef = useModalDialog(onClose);

    const items = useMemo(() => data?.items || [], [data]);
    const total = data?.total;

    // costos aún no en la composición (agregar = costo nuevo; la cantidad de uno ya
    // presente se cambia con su edición inline).
    const usedIds = useMemo(() => new Set(items.map((l) => l.cost?.id)), [items]);
    const available = (costsData?.items || []).filter((c) => !usedIds.has(c.id));

    const [costId, setCostId] = useState('');
    const [qty, setQty] = useState('1');
    const [editId, setEditId] = useState(null);
    const [editQty, setEditQty] = useState('1');

    const addLine = async () => {
        if (!costId) { toast.error('Elegí un costo'); return; }
        const n = Number(qty);
        if (!(n > 0)) { toast.error('La cantidad debe ser mayor a 0'); return; }
        try {
            await add({ cost_id: Number(costId), quantity: n });
            toast.success('Costo agregado');
            setCostId(''); setQty('1');
        } catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo agregar'); }
    };

    const startEdit = (l) => { setEditId(l.id); setEditQty(String(l.quantity ?? 1)); };
    const saveEdit = async (l) => {
        const n = Number(editQty);
        if (!(n > 0)) { toast.error('La cantidad debe ser mayor a 0'); return; }
        try {
            await update({ lineId: l.id, quantity: n });
            toast.success('Cantidad actualizada');
            setEditId(null);
        } catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo actualizar'); }
    };

    const del = async (l) => {
        try { await remove(l.id); toast.success('Costo quitado'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo quitar'); }
    };

    return (
        <div className="billing-modal" role="dialog" aria-modal="true" aria-label="Composición del manejo">
            <div className="billing-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="billing-modal__card billing-modal__card--wide" ref={cardRef}>
                <header className="billing-modal__head">
                    <div>
                        <h2 className="billing-modal__title">Composición</h2>
                        <span className="billing-modal__sub">{management.name} · el total es informativo (no es la tarifa)</span>
                    </div>
                    <button type="button" className="billing-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                {error ? (
                    <p className="billing-modal__error">No se pudo cargar la composición.</p>
                ) : isLoading ? (
                    <p className="billing-modal__state">Cargando…</p>
                ) : (
                    <table className="cat-table billing-lines">
                        <thead>
                            <tr><th>Costo</th><th>Monto</th><th>Cantidad</th><th>Subtotal</th><th aria-label="Acciones" /></tr>
                        </thead>
                        <tbody>
                            {items.map((l) => (
                                <tr key={l.id} className="cat-table__row">
                                    <td style={{ textAlign: 'left' }}>
                                        {l.cost?.name || `#${l.cost?.id}`}
                                        {l.cost?.quantitative && <span className="billing-lines__unit"> · {magnitudeLabel(l.cost?.magnitude)}</span>}
                                    </td>
                                    <td>{fmtMoney(l.cost?.amount)}</td>
                                    <td>
                                        {editId === l.id ? (
                                            <input className="billing-lines__qty" type="number" min="0" step="any" value={editQty} onChange={(e) => setEditQty(e.target.value)} disabled={busy} />
                                        ) : (
                                            l.cost?.quantitative ? (l.quantity ?? 1) : <span className="cat-table__empty">—</span>
                                        )}
                                    </td>
                                    <td>{fmtMoney(l.subtotal)}</td>
                                    <td>
                                        <span className="billing-lines__tools">
                                            {editId === l.id ? (
                                                <>
                                                    <button type="button" className="billing-btn billing-btn--ghost billing-btn--sm" onClick={() => setEditId(null)} disabled={busy}>Cancelar</button>
                                                    <button type="button" className="billing-btn billing-btn--primary billing-btn--sm" onClick={() => saveEdit(l)} disabled={busy}>Guardar</button>
                                                </>
                                            ) : (
                                                <>
                                                    {l.cost?.quantitative && <button type="button" className="billing-icon" onClick={() => startEdit(l)} disabled={busy} aria-label="Editar cantidad" title="Editar cantidad"><IconEdit /></button>}
                                                    <button type="button" className="billing-icon billing-icon--danger" onClick={() => del(l)} disabled={busy} aria-label="Quitar" title="Quitar"><IconTrash /></button>
                                                </>
                                            )}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {!items.length && <tr><td colSpan={5} className="cat-table__empty" style={{ textAlign: 'center' }}>Sin costos en la composición.</td></tr>}
                        </tbody>
                        {items.length > 0 && (
                            <tfoot>
                                <tr><td colSpan={3} style={{ textAlign: 'right' }}>Total (informativo)</td><td>{fmtMoney(total)}</td><td /></tr>
                            </tfoot>
                        )}
                    </table>
                )}

                <div className="billing-add">
                    <span className="billing-add__title">Agregar costo</span>
                    {!available.length ? (
                        <p className="billing-add__empty">No hay costos disponibles para agregar.</p>
                    ) : (
                        <div className="billing-add__row">
                            <select value={costId} onChange={(e) => setCostId(e.target.value)} disabled={busy}>
                                <option value="">Elegí un costo…</option>
                                {available.map((c) => <option key={c.id} value={c.id}>{c.name} · {fmtMoney(c.amount)}{c.quantitative ? ` /${magnitudeLabel(c.magnitude)}` : ''}</option>)}
                            </select>
                            <input className="billing-lines__qty" type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} disabled={busy} aria-label="Cantidad" />
                            <button type="button" className="billing-btn billing-btn--primary" onClick={addLine} disabled={busy || !costId}><IconPlus /> Agregar</button>
                        </div>
                    )}
                </div>

                <footer className="billing-modal__foot">
                    <button type="button" className="billing-btn billing-btn--ghost" onClick={onClose}>Cerrar</button>
                </footer>
            </div>
        </div>
    );
}
